import React, { useMemo, useState } from 'react';
import CanvasJSReact from '../../Assests/canvasjs.react';
import './Folliculogram.css';

const { CanvasJSChart } = CanvasJSReact;

// ── The folliculogram, for the patient to READ ───────────────────────────────
//
// The patient portal never edits a folliculogram — the clinic does that in the
// OXAR hub. So this is a view: the grid of scan days, and the two graphs drawn
// from it underneath. It replaces a table that was built as one long HTML
// string and pushed into the page with jQuery, and two charts drawn into divs
// by id at a fixed 1366px width.
//
//   - VERSIONS. Every save in the hub can be kept as a version. The picker
//     lists them and opens on the LATEST, which is the one that matters; an
//     older one is there to be looked at, and the page says so while it is.
//   - THE GRID. Scan days across, follicle sizes down, right ovary in red and
//     left in blue, then hormones, then drugs. The row names and the dates stay
//     pinned while it scrolls, and the row and column under the pointer light
//     up, so a number can be read against its size and its day without a ruler.
//     Empty size rows can be hidden; each block (sizes, hormones, drugs) folds.
//   - THE GRAPHS, below: hormone levels by day, and follicle sizes by day with
//     the bubble size showing how many.
//
// Data shape (one scan day), as the hub saves it:
//   { date: "1-May-2025", Values: [{ Col_R, Col_l } × 20], E2, LH, p4, FSH,
//     PROL, Endome, DrugsValue: [{ DrugName, DrugsVAlues }], ... }
// Values run from the top of the grid down: >25, 25 … 10, 6-9, 2-5, Total.

// The size rows, top to bottom, with where each sits on the bubble graph.
const SIZE_ROWS = [
  { label: '>25', y: 26 },
  ...Array.from({ length: 16 }, (_, i) => ({ label: String(25 - i), y: 25 - i })),
  { label: '6-9', y: 7.5 },
  { label: '2-5', y: 3.5 },
  { label: 'Total', y: null, total: true },
];

const HORMONES = [
  { key: 'Endome', label: 'Endometrium' },
  { key: 'E2', label: 'E2' },
  { key: 'LH', label: 'LH' },
  { key: 'p4', label: 'P4' },
  { key: 'FSH', label: 'FSH' },
  { key: 'PROL', label: 'Prol' },
];

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const hasValue = (v) => v !== null && v !== undefined && String(v).trim() !== '';
const toNumber = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// A scan day's date. The hub writes "1-May-2025", which Chrome's Date will
// read but Safari and Firefox will not — so that shape is read by hand, and
// only anything else is left to Date.
export function parseFolliDate(text) {
  if (!text) return null;
  const m = /^\s*(\d{1,2})[-\s]([A-Za-z]{3})[A-Za-z]*[-\s](\d{4})\s*$/.exec(String(text));
  if (m) {
    const month = MONTHS.findIndex((name) => name.toLowerCase() === m[2].toLowerCase());
    if (month >= 0) return new Date(Number(m[3]), month, Number(m[1]));
  }
  const d = new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}

const parseDay = parseFolliDate;

function shortDate(d) {
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : '';
}

function longDate(value) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return `${d.getDate()}-${MONTHS[d.getMonth()]}-${d.getFullYear()}`;
}

// ── Reading the saved JSON into versions ────────────────────────────────────
//
// folliculagramJSON has been saved as the bare list of days, and as that list
// wrapped in { JsonObject: [...] }. Where versions are kept, they are a list
// of { version, savedOn, savedBy, JsonObject }. All of these are read here into
// one shape — [{ number, savedOn, savedBy, days }] oldest first — so the grid
// never has to care which one it was given. Anything unreadable is no versions
// rather than a crash.

function parseJson(json) {
  if (!json) return null;
  try {
    let parsed = typeof json === 'string' ? JSON.parse(json) : json;
    // Saved twice (or more) encoded, it parses to a string the first time.
    for (let i = 0; i < 3 && typeof parsed === 'string'; i += 1) parsed = JSON.parse(parsed);
    return parsed;
  } catch {
    return null;
  }
}

// A key found by name, whatever its case: the hub has saved "date" and "Date",
// "Values" and "values".
const keyOf = (obj, name) => Object.keys(obj || {}).find((k) => k.toLowerCase() === name.toLowerCase());

const looksLikeDay = (d) => d && typeof d === 'object' && keyOf(d, 'date') !== undefined && keyOf(d, 'values') !== undefined;
const isDayList = (list) => Array.isArray(list) && list.some(looksLikeDay);

// One scan day, with the keys the grid reads (date, Values, DrugsValue, E2 ...)
// whatever case they were saved in.
const CANON = ['date', 'Values', 'DrugsValue', 'E2', 'LH', 'p4', 'FSH', 'PROL', 'Endome'];
function canonDay(d) {
  if (!d || typeof d !== 'object') return d;
  const out = { ...d };
  CANON.forEach((name) => {
    if (out[name] === undefined) {
      const k = keyOf(d, name);
      if (k !== undefined) out[name] = d[k];
    }
  });
  // Values saved as a JSON string.
  if (typeof out.Values === 'string') out.Values = parseJson(out.Values) || [];
  if (typeof out.DrugsValue === 'string') out.DrugsValue = parseJson(out.DrugsValue) || [];
  return out;
}

const asList = (value) => {
  const v = typeof value === 'string' ? parseJson(value) : value;
  return isDayList(v) ? v.map(canonDay) : null;
};

function daysOf(item) {
  const direct = asList(item);
  if (direct) return direct;
  if (!item || typeof item !== 'object') return null;
  for (const key of ['JsonObject', 'jsonObject', 'Days', 'days', 'Data', 'data', 'Json', 'json', 'Grid', 'grid']) {
    const found = asList(item[key]);
    if (found) return found;
  }
  return null;
}

// Last resort: the day list is somewhere in the saved structure, however it was
// wrapped. The largest one found is the grid.
function deepDays(node, depth = 0) {
  if (depth > 6 || node === null || node === undefined) return null;
  const value = typeof node === 'string' && /^\s*[[{]/.test(node) ? parseJson(node) : node;
  const direct = asList(value);
  if (direct) return direct;
  if (!value || typeof value !== 'object') return null;
  let best = null;
  Object.values(value).forEach((child) => {
    const found = deepDays(child, depth + 1);
    if (found && (!best || found.length > best.length)) best = found;
  });
  return best;
}

function versionMeta(item, index) {
  const pick = (...keys) => keys.map((k) => item?.[k]).find(hasValue);
  const number = toNumber(pick('Version', 'version', 'VersionNo', 'versionNo', 'VersionNumber', 'versionNumber'));
  return {
    number: number ?? index + 1,
    savedOn: pick('SavedOn', 'savedOn', 'ModifiedOn', 'modifiedOn', 'CreatedOn', 'createdOn', 'Date'),
    savedBy: pick('SavedBy', 'savedBy', 'ModifiedBy', 'modifiedBy', 'CreatedBy', 'createdBy'),
    label: pick('Name', 'name', 'Label', 'label'),
  };
}

export function readFolliculogramVersions(json) {
  const parsed = parseJson(json);
  if (!parsed) return [];

  let versions = [];
  const listed = Array.isArray(parsed) && !isDayList(parsed)
    ? parsed
    : parsed.Versions ?? parsed.versions ?? parsed.History ?? parsed.history ?? parsed.Folliculograms ?? parsed.folliculograms ?? null;

  if (Array.isArray(listed)) {
    versions = listed
      .map((item, i) => ({ ...versionMeta(item, i), days: daysOf(item) }))
      .filter((v) => v.days);
  }

  // The current grid, when it sits beside the history rather than in it.
  const current = daysOf(parsed);
  if (current && !versions.some((v) => v.days === current)) {
    versions.push({ ...versionMeta(parsed, versions.length), days: current, current: true });
  }

  if (!versions.length) {
    const found = deepDays(parsed);
    if (found) versions.push({ ...versionMeta(parsed, 0), days: found, current: true });
  }

  versions = versions
    .map((v) => ({ ...v, days: v.days.filter((d) => d && hasValue(d.date)) }))
    .filter((v) => v.days.length > 0)
    .sort((a, b) => a.number - b.number);

  // Numbered 1..n in saved order if the source did not number them uniquely.
  const numbers = new Set(versions.map((v) => v.number));
  if (numbers.size !== versions.length) versions = versions.map((v, i) => ({ ...v, number: i + 1 }));
  return versions;
}

// ── The grid ────────────────────────────────────────────────────────────────

function drugNames(days) {
  const names = [];
  days.forEach((day) =>
    (day.DrugsValue || []).forEach((drug) => {
      const name = drug?.DrugName;
      if (hasValue(name) && !names.includes(name)) names.push(name);
    }),
  );
  return names;
}

function drugValue(day, name, index) {
  const list = day.DrugsValue || [];
  const byName = list.find((d) => d?.DrugName === name);
  const drug = byName || list[index];
  return drug?.DrugsVAlues ?? drug?.DrugsValues ?? '';
}

function SectionRow({ label, span, open, onToggle, count }) {
  return (
    <tr className="folli-section-row">
      <th className="folli-sticky" scope="rowgroup">
        <button type="button" className="folli-fold" onClick={onToggle} aria-expanded={open}>
          <span aria-hidden="true">{open ? '▾' : '▸'}</span> {label}
          {count !== undefined && <em>{count}</em>}
        </button>
      </th>
      <td colSpan={span} />
    </tr>
  );
}

const DAY_NAMES = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MONTH_CAPS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

// markers: [{ date: Date, kind: 'visit' | 'key', label, icon, title }] — the
// booked appointments and the key dates of the cycle. They show as icons
// under each day's heading, and as chips in the "Suggested" lane on the grid.
function Grid({ days, markers = [] }) {
  const [hideEmpty, setHideEmpty] = useState(true);
  const [onGrid, setOnGrid] = useState(true);
  const [open, setOpen] = useState({ sizes: true, hormones: true, drugs: true });
  const [hover, setHover] = useState({ row: null, col: null });

  const allDates = useMemo(() => days.map((d) => parseDay(d.date)), [days]);
  const drugs = useMemo(() => drugNames(days), [days]);
  const toggle = (key) => setOpen((o) => ({ ...o, [key]: !o[key] }));

  // Markers by the scan day they fall on.
  const markersByDay = useMemo(
    () => allDates.map((d) => (d ? markers.filter((m) => sameDay(m.date, d)) : [])),
    [allDates, markers],
  );

  // A day is empty when nothing is recorded on it and nothing is booked.
  const dayHasData = (day, i) =>
    markersByDay[i].length > 0 ||
    SIZE_ROWS.some((_, r) => hasValue(day.Values?.[r]?.Col_R) || hasValue(day.Values?.[r]?.Col_l)) ||
    HORMONES.some((h) => hasValue(day[h.key])) ||
    drugs.some((n, di) => hasValue(drugValue(day, n, di)));

  const shownDays = days.map((_, i) => i).filter((i) => !hideEmpty || dayHasData(days[i], i));
  const hiddenDays = days.length - shownDays.length;
  const sd = shownDays.map((i) => days[i]);          // the days drawn
  const dates = shownDays.map((i) => allDates[i]);
  const dayMarkers = shownDays.map((i) => markersByDay[i]);

  const sizeRows = SIZE_ROWS.map((row, i) => ({
    ...row,
    index: i,
    filled: sd.some((d) => hasValue(d.Values?.[i]?.Col_R) || hasValue(d.Values?.[i]?.Col_l)),
  }));
  const shownSizes = hideEmpty ? sizeRows.filter((r) => r.filled || r.total) : sizeRows;
  const filledSizes = sizeRows.filter((r) => r.filled && !r.total).length;
  const shownHormones = hideEmpty ? HORMONES.filter((h) => sd.some((d) => hasValue(d[h.key]))) : HORMONES;
  const shownDrugs = hideEmpty ? drugs.filter((n) => sd.some((d) => hasValue(drugValue(d, n, drugs.indexOf(n))))) : drugs;

  // The Suggested lane: one chip per marker, stacked into as few lines as it takes.
  const lanes = [];
  dayMarkers.forEach((list, col) => list.forEach((m, k) => {
    while (lanes.length <= k) lanes.push({});
    lanes[k][col] = m;
  }));

  const span = sd.length * 2;
  const cross = (row, col) => ({
    onMouseEnter: () => setHover({ row, col }),
  });
  const cellClass = (row, col, extra = '') =>
    [extra, hover.row === row ? 'is-cross-row' : '', hover.col === col ? 'is-cross-col' : '']
      .filter(Boolean)
      .join(' ');

  return (
    <>
      <div className="folli-grid-tools">
        <label className="folli-check">
          <input type="checkbox" checked={hideEmpty} onChange={(e) => setHideEmpty(e.target.checked)} />
          <span>Hide empty</span>
        </label>
        {hiddenDays > 0 && <span className="folli-legend">{hiddenDays} day(s) hidden</span>}
        <label className="folli-check">
          <input type="checkbox" checked={onGrid} onChange={(e) => setOnGrid(e.target.checked)} />
          <span>On the grid</span>
        </label>
        <span className="folli-legend">
          <i className="is-r" /> Right ovary <i className="is-l" /> Left ovary
        </span>
      </div>

      <div className="folli-grid-scroll" onMouseLeave={() => setHover({ row: null, col: null })}>
        <table className="folli-grid">
          <thead>
            <tr className="folli-h1">
              <th className="folli-sticky">Day</th>
              {shownDays.map((di, i) => (
                <th key={di} colSpan={2} className={cellClass(null, i, 'folli-dayno')}>
                  D{di + 1}
                  {dayMarkers[i].map((m, k) => (
                    <i key={k} className={`fa-solid ${m.icon || 'fa-calendar-check'} folli-pin is-${m.kind}`} title={m.title || m.label} aria-label={m.label} />
                  ))}
                </th>
              ))}
            </tr>
            <tr className="folli-h2">
              <th className="folli-sticky">Date</th>
              {shownDays.map((di, i) => {
                const d = dates[i];
                return (
                  <th key={di} colSpan={2} className={cellClass(null, i, 'folli-date')} title={days[di].date}>
                    <span>DAY {di + 1} {d ? `(${DAY_NAMES[d.getDay()]})` : ''}</span>
                    <b>{d ? `${String(d.getDate()).padStart(2, '0')}-${MONTH_CAPS[d.getMonth()]}-${d.getFullYear()}` : days[di].date}</b>
                  </th>
                );
              })}
            </tr>
            <tr className="folli-h3">
              <th className="folli-sticky">In mm</th>
              {shownDays.map((di, i) => (
                <React.Fragment key={di}>
                  <th className={cellClass(null, i, 'folli-r')}>R</th>
                  <th className={cellClass(null, i, 'folli-l')}>L</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>

          <tbody>
            {onGrid && lanes.length > 0 && lanes.map((lane, li) => (
              <tr key={`lane${li}`} className="folli-lane">
                <th className="folli-sticky" scope="row">{li === 0 ? 'Suggested' : ''}</th>
                {shownDays.map((di, col) => {
                  const m = lane[col];
                  return (
                    <td key={di} colSpan={2} className={cellClass(null, col, 'folli-lanecell')}>
                      {m && <span className={`folli-chip is-${m.kind}`} title={m.title || m.label}><i className={`fa-solid ${m.icon || 'fa-calendar-check'}`} aria-hidden="true" /> {m.label}</span>}
                    </td>
                  );
                })}
              </tr>
            ))}

            <SectionRow label="Follicles" span={span} open={open.sizes} onToggle={() => toggle('sizes')} count={`${filledSizes} sizes recorded`} />
            {open.sizes &&
              shownSizes.map((row) => {
                const key = `s${row.index}`;
                return (
                  <tr key={key} className={row.total ? 'folli-total-row' : undefined}>
                    <th className={cellClass(key, null, 'folli-sticky')} scope="row">
                      {row.label}
                    </th>
                    {sd.map((day, i) => {
                      const r = day.Values?.[row.index]?.Col_R;
                      const l = day.Values?.[row.index]?.Col_l;
                      return (
                        <React.Fragment key={i}>
                          <td {...cross(key, i)} className={cellClass(key, i, `folli-r${hasValue(r) ? ' is-filled' : ''}`)}>
                            {r}
                          </td>
                          <td {...cross(key, i)} className={cellClass(key, i, `folli-l${hasValue(l) ? ' is-filled' : ''}`)}>
                            {l}
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}

            <SectionRow label="Hormones" span={span} open={open.hormones} onToggle={() => toggle('hormones')} />
            {open.hormones &&
              shownHormones.map((h) => {
                const key = `h${h.key}`;
                return (
                  <tr key={key}>
                    <th className={cellClass(key, null, 'folli-sticky')} scope="row">
                      {h.label}
                    </th>
                    {sd.map((day, i) => (
                      <td
                        key={i}
                        colSpan={2}
                        {...cross(key, i)}
                        className={cellClass(key, i, `folli-wide${hasValue(day[h.key]) ? ' is-filled' : ''}`)}
                      >
                        {day[h.key]}
                      </td>
                    ))}
                  </tr>
                );
              })}

            {drugs.length > 0 && (
              <SectionRow label="Drugs" span={span} open={open.drugs} onToggle={() => toggle('drugs')} />
            )}
            {open.drugs &&
              shownDrugs.map((name) => {
                const key = `d${name}`;
                const index = drugs.indexOf(name);
                return (
                  <tr key={key}>
                    <th className={cellClass(key, null, 'folli-sticky folli-drug')} scope="row" title={name}>
                      {name}
                    </th>
                    {sd.map((day, i) => {
                      const v = drugValue(day, name, index);
                      return (
                        <td
                          key={i}
                          colSpan={2}
                          {...cross(key, i)}
                          className={cellClass(key, i, `folli-wide${hasValue(v) ? ' is-filled' : ''}`)}
                        >
                          {v}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ── The graphs ──────────────────────────────────────────────────────────────

function HormoneChart({ days }) {
  // E2 is in the thousands where the others are single figures, so it is
  // drawn in thousands to share the axis — and the legend says so.
  const series = [
    { key: 'E2', name: 'E2 (÷1000)', scale: 1 / 1000, color: '#e34948' },
    { key: 'LH', name: 'LH', color: '#2a78d6' },
    { key: 'p4', name: 'P4', color: '#1baf7a' },
    { key: 'FSH', name: 'FSH', color: '#eda100' },
    { key: 'PROL', name: 'Prol', color: '#4a3aa7' },
  ];
  const data = series
    .map((s) => ({
      type: 'line',
      name: s.name,
      color: s.color,
      showInLegend: true,
      connectNullData: true,
      markerSize: 7,
      yValueFormatString: '#0.##',
      dataPoints: days.map((day, i) => {
        const n = toNumber(day[s.key]);
        return { x: i + 1, y: n === null ? null : n * (s.scale || 1), label: day.date };
      }),
    }))
    .filter((s) => s.dataPoints.some((p) => p.y !== null));

  if (data.length === 0) return <p className="folli-empty">No hormone results have been recorded on this folliculogram.</p>;

  const options = {
    animationEnabled: true,
    zoomEnabled: true,
    height: 340,
    toolTip: { shared: true },
    legend: { cursor: 'pointer', fontSize: 13 },
    axisX: { title: 'Day of treatment', interval: 1, labelFontSize: 12, titleFontSize: 13 },
    axisY: { title: 'Level', includeZero: false, labelFontSize: 12, titleFontSize: 13, gridColor: '#e4ecea' },
    data,
  };
  return <CanvasJSChart options={options} />;
}

function FollicleChart({ days }) {
  const points = [];
  days.forEach((day, i) =>
    SIZE_ROWS.forEach((row, r) => {
      if (row.total) return;
      [
        ['Col_R', 'Right', '#e34948'],
        ['Col_l', 'Left', '#2a78d6'],
      ].forEach(([col, side, color]) => {
        const count = toNumber(day.Values?.[r]?.[col]);
        if (count) points.push({ x: i + 1, y: row.y, z: count, color, name: `${day.date} · ${side}`, size: row.label });
      });
    }),
  );

  if (points.length === 0) return <p className="folli-empty">No follicle measurements have been recorded on this folliculogram.</p>;

  const options = {
    animationEnabled: true,
    zoomEnabled: true,
    height: 460,
    axisX: { title: 'Day of treatment', interval: 1, minimum: 0.5, maximum: days.length + 0.5, labelFontSize: 12, titleFontSize: 13 },
    axisY: { title: 'Follicle size (mm)', minimum: 0, maximum: 28, labelFontSize: 12, titleFontSize: 13, gridColor: '#e4ecea' },
    data: [
      {
        type: 'bubble',
        fillOpacity: 0.55,
        toolTipContent: '<b>{name}</b><br/>Day of treatment: {x}<br/>Size: {size} mm<br/>Follicles: {z}',
        dataPoints: points,
      },
    ],
  };
  return <CanvasJSChart options={options} />;
}

// ── The whole thing ─────────────────────────────────────────────────────────

export default function Folliculogram({ json, markers = [], children = null }) {
  const versions = useMemo(() => readFolliculogramVersions(json), [json]);
  const latest = versions.length ? versions[versions.length - 1].number : null;
  const [picked, setPicked] = useState(null);
  const [showCharts, setShowCharts] = useState(true);

  // Always opens on the latest; a version chosen earlier that no longer exists
  // (the data was refreshed) falls back to the latest too.
  const version = versions.find((v) => v.number === picked) || versions[versions.length - 1];

  if (!version) {
    return (
      <section className="folli">
        <header className="folli-head">
          <h5>Folliculogram</h5>
        </header>
        <p className="folli-empty">No folliculogram has been recorded for this treatment yet.</p>
      </section>
    );
  }

  const days = version.days;
  const first = parseDay(days[0].date);
  const last = parseDay(days[days.length - 1].date);
  const isLatest = version.number === latest;

  return (
    <section className="folli">
      <header className="folli-head">
        <h5>Folliculogram</h5>
        <span className="folli-readonly">Read only</span>

        <label className="folli-version">
          {/* <span>Version</span>
          <select value={version.number} onChange={(e) => setPicked(Number(e.target.value))} disabled={versions.length < 2}>
            {[...versions].reverse().map((v) => (
              <option key={v.number} value={v.number}>
                {`Version ${v.number}`}
                {v.number === latest ? ' · latest' : ''}
                {v.savedOn ? ` · ${longDate(v.savedOn)}` : ''}
                {v.label ? ` · ${v.label}` : ''}
              </option>
            ))}
          </select>
          <em>{versions.length === 1 ? '1 version' : `${versions.length} versions`}</em> */}
        </label>

        <span className="folli-span">
          {longDate(first) || days[0].date} – {longDate(last) || days[days.length - 1].date}
          <em>{days.length} {days.length === 1 ? 'day' : 'days'}</em>
        </span>
      </header>

      {!isLatest && (
        <div className="folli-old" role="status">
          You are looking at version {version.number}
          {version.savedOn ? `, saved ${longDate(version.savedOn)}` : ''}
          {version.savedBy ? ` by ${version.savedBy}` : ''}. It is not the latest.
          <button type="button" onClick={() => setPicked(latest)}>
            Show the latest
          </button>
        </div>
      )}

      {/* Keyed by version so the grid's folds and hover start fresh. */}
      <Grid key={`g${version.number}`} days={days} markers={markers} />

      {children}

      <div className="folli-charts-head">
        <h5>Graphs</h5>
        <button type="button" className="folli-link" onClick={() => setShowCharts((s) => !s)}>
          {showCharts ? 'Hide graphs' : 'Show graphs'}
        </button>
      </div>
      {showCharts && (
        <div className="folli-charts" key={`c${version.number}`}>
          <figure className="folli-chart">
            <figcaption>Hormone levels</figcaption>
            <HormoneChart days={days} />
          </figure>
          <figure className="folli-chart">
            <figcaption>
              Follicle size by day <em>— the bigger the bubble, the more follicles of that size</em>
            </figcaption>
            <FollicleChart days={days} />
          </figure>
        </div>
      )}
    </section>
  );
}
